import { maunaRewardAPI } from '@/api';
import { LoadingMessage } from '@/components/CommonStyles';
import { palette } from '@/modules/defines/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import MaunaHeader from './MaunaHeader';

interface Reward {
  id: number;
  title: string;
  category: number;
  description: string;
  img: string | null;
  sid: number | null;
  adminCode: string | null;
  createdAt: string;
  updatedAt: string;
}

const categoryMap: Record<number, string> = {
  1: '커피',
  2: '시설이용 할인권',
  3: '굿즈',
};

const decodeAdminCode = (value?: string | null) => {
  if (!value) return '';

  try {
    const binary = window.atob(value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const decoded = new TextDecoder().decode(bytes);

    return decoded.includes('\uFFFD') ? value : decoded;
  } catch {
    return value;
  }
};

const MaunaRewardDetailContainer = () => {
  const { id } = useParams();
  const rewardId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<Reward>>({});

  const { data: rewardsData, isLoading } = useQuery({
    queryKey: ['getRewardsMaunaAdmin'],
    queryFn: () =>
      maunaRewardAPI.getRewards({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
  });

  const rewards = useMemo(() => {
    const payload = rewardsData?.data;
    const list =
      payload?.data?.data ?? payload?.data ?? payload?.result ?? payload ?? [];
    return Array.isArray(list) ? list : [];
  }, [rewardsData?.data]);

  const reward = useMemo<Reward | undefined>(() => {
    return rewards.find((item: Reward) => item.id === rewardId);
  }, [rewards, rewardId]);

  useEffect(() => {
    if (reward) {
      setFormData({
        ...reward,
        adminCode: decodeAdminCode(reward.adminCode),
      });
    }
  }, [reward]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Reward>) =>
      maunaRewardAPI.updateReward(
        {
          code: import.meta.env.VITE_SECRET_API_KEY,
          id: rewardId,
        },
        data
      ),
    onSuccess: () => {
      alert('수정되었습니다');
      queryClient.invalidateQueries({ queryKey: ['getRewardsMaunaAdmin'] });
      queryClient.invalidateQueries({ queryKey: ['getRewardsMauna'] });
    },
  });

  const handleChange = <K extends keyof Reward>(
    name: K,
    value: Reward[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    updateMutation.mutate({
      ...formData,
      id: rewardId,
    });
  };

  if (isLoading) {
    return (
      <Container>
        <MaunaHeader />
        <Content>
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        </Content>
      </Container>
    );
  }

  if (!reward) {
    return (
      <Container>
        <MaunaHeader />
        <Content>
          <LoadingMessage>리워드 데이터가 없습니다.</LoadingMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <MaunaHeader />
      <Content>
        <HeaderRow>
          <HeaderTitle>리워드 상세</HeaderTitle>
          <HeaderActions>
            <SecondaryButton type="button" onClick={() => navigate(-1)}>
              목록
            </SecondaryButton>
            <PrimaryButton type="button" onClick={handleSave}>
              저장
            </PrimaryButton>
          </HeaderActions>
        </HeaderRow>
        <FormSection>
          <FormGroup>
            <Label>리워드명</Label>
            <Input
              value={formData.title || ''}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </FormGroup>
          <TwoColumn>
            <FormGroup>
              <Label>카테고리</Label>
              <Select
                value={String(formData.category ?? 1)}
                onChange={(e) =>
                  handleChange('category', Number(e.target.value))
                }
              >
                {Object.entries(categoryMap).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>공간 ID</Label>
              <Input
                type="number"
                value={formData.sid ?? ''}
                onChange={(e) =>
                  handleChange(
                    'sid',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </FormGroup>
          </TwoColumn>
          <FormGroup>
            <Label>설명</Label>
            <TextArea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>이미지 링크</Label>
            <Input
              value={formData.img || ''}
              onChange={(e) => handleChange('img', e.target.value)}
            />
            {formData.img && (
              <LinkPreview href={formData.img} target="_blank" rel="noreferrer">
                링크 열기
              </LinkPreview>
            )}
          </FormGroup>
          <FormGroup>
            <Label>승인 코드</Label>
            <Input
              value={formData.adminCode || ''}
              onChange={(e) => handleChange('adminCode', e.target.value)}
            />
          </FormGroup>
          <TwoColumn>
            <FormGroup>
              <Label>생성 날짜</Label>
              <Input
                value={
                  formData.createdAt
                    ? new Date(formData.createdAt).toLocaleDateString()
                    : ''
                }
                readOnly
              />
            </FormGroup>
            <FormGroup>
              <Label>수정 날짜</Label>
              <Input
                value={
                  formData.updatedAt
                    ? new Date(formData.updatedAt).toLocaleDateString()
                    : ''
                }
                readOnly
              />
            </FormGroup>
          </TwoColumn>
        </FormSection>
      </Content>
    </Container>
  );
};

export default MaunaRewardDetailContainer;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
`;

const Content = styled.main`
  flex: 1;
  padding: 2rem;
  background-color: #f5f5f5;
  overflow-y: auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 6px;
  padding: 0.6rem 1.2rem;
  background-color: ${palette.systemGreen};
  color: white;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #3e8e41;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.6rem 1.2rem;
  background-color: white;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
`;

const FormSection = styled.div`
  max-width: 900px;
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: ${palette.systemGreen};
  }
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  min-height: 140px;
  resize: vertical;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: ${palette.systemGreen};
  }
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${palette.systemGreen};
  }
`;

const LinkPreview = styled.a`
  font-size: 0.85rem;
  color: ${palette.systemBlue};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
